<script lang="ts">
  import { createMutation } from "@tanstack/svelte-query";
  import config from "@/config";
  import cn from "classnames";
  import { IconWand } from "@tabler/icons-svelte";
  import { getNotificationsContext } from "svelte-notifications";
  import { defaultNotificationParams } from "@/lib/constants/notifications";
  import autosize from "svelte-autosize";
  import ResultSection from "@/lib/components/result-section/result-section.svelte";
  import Disclaimer from "@/lib/components/disclaimer/disclaimer.svelte";
  import RecaptchaText from "@/lib/components/recaptcha-text/recaptcha-text.svelte";
  import { generateCompletion } from "@/lib/api/completion";
  import type { GenerateCompletionPayload } from "@/lib/api/completion";
  import LimitsCta from "@/lib/components/limits-cta/limits-cta.svelte";
  import { blur } from "svelte/transition";

  const { addNotification } = getNotificationsContext();
  export let type: "improve" | "fix" | "shorten";

  const dataByType = {
    improve: { toolName: "AI Text Enhancer" },
    fix: { toolName: "AI Text Proofreader" },
    shorten: { toolName: "AI Text Shortener" },
  };
  let result = "";

  let showLimitsCta = false;

  const maxLength = 2000;
  let text = "";
  $: isTextTooLong = text.length > maxLength;

  let formRef: HTMLDivElement;
  let containerWidth = "auto";
  let containerHeight = "auto";

  const generateMutation = createMutation<
    void,
    Error,
    GenerateCompletionPayload
  >({
    mutationFn: async (data) => {
      const completionResponse = await generateCompletion(data);

      if (completionResponse.statusCode === 429) {
        const rect = formRef.getBoundingClientRect();
        containerWidth = rect.width + "px";
        containerHeight = rect.height + "px";

        showLimitsCta = true;
        return;
      }
      if (completionResponse.result === "error") {
        addNotification({
          ...defaultNotificationParams,
          text: completionResponse.message,
        });
        return;
      }

      result = completionResponse.output;
    },
    onError: (error) => {
      addNotification({
        ...defaultNotificationParams,
        text: error.message,
      });
    },
  });

  const handleOnSubmit = async (e: SubmitEvent) => {
    const form = e.target as HTMLFormElement;
    const captchaToken = await window.grecaptcha.execute(
      config.captchaSiteKey,
      { action: "submit" },
    );

    $generateMutation.mutate({
      type,
      text: form.text.value,
      captchaToken,
    });
  };
</script>

<div
  class="relative"
  style:width={containerWidth}
  style:height={containerHeight}
>
  {#if showLimitsCta}
    <div
      class="absolute w-full h-full flex justify-center align-center"
      in:blur={{ duration: 500, delay: 600 }}
    >
      <div class="content-box">
        <LimitsCta toolName={dataByType[type].toolName} />
      </div>
    </div>
  {:else}
    <div bind:this={formRef} out:blur={{ duration: 500 }} class="content-box">
      <div class="font-l flex gap-5 align-center font-medium mb-10">
        <slot name="header" />
      </div>
      <form on:submit|preventDefault={handleOnSubmit}>
        <fieldset disabled={$generateMutation.isPending}>
          <p class="form-field form-hint">
            <slot name="hint" />
          </p>

          <div class="form-field">
            <label for="text-completion-text" class="form-label"> Text </label>
            <p class="form-hint mb-10">Please put your text here.</p>
            <textarea
              id="text-completion-text"
              class="form-textarea no-resize"
              name="text"
              bind:value={text}
              placeholder="e.g. Please be aware that our office is closed on this Tuesday"
              required
              rows="3"
              use:autosize
            ></textarea>
            <p
              class={cn("text-right font-s mt-5", {
                "color-red": isTextTooLong,
              })}
            >
              Characters left: {maxLength - text.length}
            </p>
          </div>

          {#if !result}
            <div class="form-field">
              <button
                disabled={isTextTooLong}
                type="submit"
                class={cn("button button_wide", {
                  button_loading: $generateMutation.isPending,
                })}
              >
                <IconWand class="button__icon_left button__icon" />
                Generate
              </button>
            </div>
          {:else}
            <ResultSection {result} />
          {/if}
          <Disclaimer />
          <RecaptchaText />
        </fieldset>
      </form>
    </div>
  {/if}
</div>
